import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { Check, FileText, GitBranch, Lock } from "lucide-react";

import { Skeleton } from "../feedback/skeleton";
import { StatusBadge } from "../feedback/status-badge";
import { formatElapsed, useElapsed } from "../hooks/use-elapsed";
import { cn } from "../utils";
import { GitHubIcon } from "./provider-icons";

export interface GitHubRepositoryCreationProps {
  owner: string;
  name: string;
  /** `creating` while the request is in flight, `created` once GitHub has answered. */
  status: "creating" | "created";
  /** ISO time the request started; drives the elapsed counter. */
  startedAt: string;
  /** Known only once GitHub answers, so it is never shown while creating. */
  defaultBranch?: string;
  /** What to do next, shown once the repository exists. */
  children?: ReactNode;
  /** The product shows `blueprint`; `contributions` is an alternative kept for comparison in Storybook. */
  scene?: "blueprint" | "contributions";
  className?: string;
}

const ROWS = 7;
const COLUMNS = 27;
const CELL = 10;
const PITCH = 13;
const WAVE_MS = 2800;

/** Top cell of each column of the check the grid settles into, in stroke order; the stroke is two cells tall. */
const CHECK_STROKE: [row: number, column: number][] = [
  [3, 9],
  [4, 10],
  [5, 11],
  [4, 12],
  [3, 13],
  [2, 14],
  [1, 15],
  [0, 16],
];
const CHECK_ORDER = new Map(
  CHECK_STROKE.flatMap(([row, column], order) => [
    [`${row}:${column}`, order] as const,
    [`${row + 1}:${column}`, order] as const,
  ]),
);

// Fixed pseudo-random levels: the grid reads as a contribution graph and never reshuffles on render.
const LEVELS = [0, 0, 0.3, 0.3, 0.55, 0.55, 0.8, 1];
function level(row: number, column: number): number {
  let hash = Math.imul(row + 1, 374761393) ^ Math.imul(column + 1, 668265263);
  hash = Math.imul(hash ^ (hash >>> 13), 1274126177);
  return LEVELS[((hash ^ (hash >>> 16)) >>> 0) % LEVELS.length] ?? 0;
}
const CELLS = Array.from({ length: ROWS * COLUMNS }, (_, index) => {
  const row = index % ROWS;
  const column = Math.floor(index / ROWS);
  return { row, column, peak: level(row, column), order: CHECK_ORDER.get(`${row}:${column}`) };
});

function ContributionGrid({ settled }: { settled: boolean }) {
  return (
    <svg
      viewBox={`0 0 ${COLUMNS * PITCH - (PITCH - CELL)} ${ROWS * PITCH - (PITCH - CELL)}`}
      className="w-full max-w-[348px]"
      aria-hidden
    >
      {CELLS.map(({ row, column, peak, order }) => {
        const box = { x: column * PITCH, y: row * PITCH, width: CELL, height: CELL, rx: 2 };
        return (
          <g key={`${row}:${column}`}>
            <rect {...box} className="fill-border" />
            {settled ? (
              order !== undefined && (
                <rect
                  {...box}
                  className="animate-node-pop origin-center fill-success [transform-box:fill-box]"
                  style={{ "--node-delay": order * 45 } as CSSProperties}
                />
              )
            ) : peak > 0 ? (
              <rect
                {...box}
                className="animate-cell-wave fill-success"
                style={
                  {
                    "--cell-peak": peak,
                    // Negative, so the crest is already crossing when the grid appears.
                    "--cell-delay": column * 80 + row * 30 - WAVE_MS,
                  } as CSSProperties
                }
              />
            ) : null}
          </g>
        );
      })}
    </svg>
  );
}

const TICK_MS = 45;
const BADGE_AT = 10;
const BRANCH_AT = 18;
const README_AT = 26;

/** Counts up while `active`; the blueprint's build-up is derived from the count. */
function useTicks(active: boolean, max: number): number {
  const [ticks, setTicks] = useState(0);
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => setTicks((count) => Math.min(count + 1, max)), TICK_MS);
    return () => clearInterval(timer);
  }, [active, max]);
  return ticks;
}

function Blueprint({
  owner,
  name,
  created,
  defaultBranch,
}: Pick<GitHubRepositoryCreationProps, "owner" | "name"> & {
  created: boolean;
  defaultBranch: string | undefined;
}) {
  const [reducedMotion] = useState(
    () =>
      typeof window !== "undefined" &&
      (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false),
  );
  const staged = !created && !reducedMotion;
  const end = Math.max(name.length, README_AT);
  const ticks = useTicks(staged, end);
  const shown = staged ? ticks : end;
  const rows = [
    {
      at: BRANCH_AT,
      icon: GitBranch,
      value: defaultBranch,
      width: "w-12",
      label: "Default branch",
    },
    { at: README_AT, icon: FileText, value: "README.md", width: "w-20", label: "First commit" },
  ];
  return (
    <div className={cn("max-w-md rounded-lg border p-4", !created && "border-dashed")}>
      <div className="flex items-center gap-3">
        <span
          className="flex size-9 shrink-0 items-center justify-center rounded-md border bg-background"
          aria-hidden
        >
          <GitHubIcon className="size-4" />
        </span>
        <div className="min-w-0 flex-1 truncate font-mono text-[13px]">
          <span className="text-muted-foreground">{owner}/</span>
          {name.slice(0, shown)}
          {staged && shown < name.length ? (
            <span className="animate-dot-pulse text-muted-foreground" aria-hidden>
              ▍
            </span>
          ) : null}
        </div>
        {shown >= BADGE_AT ? (
          <StatusBadge variant="neutral" icon={Lock} className="animate-node-pop">
            Private
          </StatusBadge>
        ) : null}
      </div>
      <ul className={cn("mt-4 space-y-2.5 border-t pt-3", !created && "border-dashed")}>
        {rows.map(({ at, icon: Icon, value, width, label }) =>
          shown >= at ? (
            <li key={label} className="animate-entrance flex items-center gap-2 text-[13px]">
              <Icon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
              {created && value ? (
                <span className="animate-entrance font-mono text-xs">{value}</span>
              ) : (
                <Skeleton className={cn("h-3 motion-reduce:animate-none", width)} />
              )}
              <span className="text-muted-foreground">{label}</span>
            </li>
          ) : null,
        )}
      </ul>
    </div>
  );
}

/** Keeps the wait for a new GitHub repository company, then settles into the result. */
export function GitHubRepositoryCreation({
  owner,
  name,
  status,
  startedAt,
  defaultBranch,
  children,
  scene = "blueprint",
  className,
}: GitHubRepositoryCreationProps) {
  const created = status === "created";
  const seconds = useElapsed(startedAt, !created);
  const repository = `${owner}/${name}`;
  return (
    <div className={cn("animate-entrance space-y-3", className)}>
      {scene === "contributions" ? (
        <ContributionGrid settled={created} />
      ) : (
        <Blueprint owner={owner} name={name} created={created} defaultBranch={defaultBranch} />
      )}
      <div role="status" className="space-y-1 text-sm">
        {created ? (
          <p className="flex items-center gap-1.5 font-medium">
            {/* The grid has already drawn its own check. */}
            {scene === "blueprint" ? (
              <Check className="animate-node-pop size-4 shrink-0 text-success" aria-hidden />
            ) : null}
            <span>
              Created{" "}
              <a
                className="underline underline-offset-2"
                target="_blank"
                rel="noreferrer"
                href={`https://github.com/${encodeURIComponent(owner)}/${encodeURIComponent(name)}`}
              >
                {repository}
              </a>
            </span>
          </p>
        ) : (
          <p className="flex items-baseline gap-2">
            <span className="text-shimmer">Creating {repository} on GitHub</span>
            {/* Hidden from the status region, which would otherwise announce every second. */}
            {seconds ? (
              <span className="text-xs text-muted-foreground tabular-nums" aria-hidden>
                {formatElapsed(seconds)}
              </span>
            ) : null}
          </p>
        )}
        {created && children ? <p className="text-muted-foreground">{children}</p> : null}
      </div>
    </div>
  );
}
